import {
  cloneElement,
  createContext,
  isValidElement,
  useContext,
  useEffect,
  useRef,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from "react";
import {
  useForm as useRHF,
  useWatch,
  Controller,
  type UseFormReturn,
  type FieldValues,
  type RegisterOptions,
} from "react-hook-form";
import Box from "@mui/material/Box";
import FormLabel from "@mui/material/FormLabel";
import FormHelperText from "@mui/material/FormHelperText";

// antd <Form> form-store (Form.useForm/Form.Item/rules/onFinish) on react-hook-form.
// antd has no MUI equivalent; RHF is already a dep. The instance is created in
// useForm() so Form.useWatch works in the parent before <Form> mounts; <Form
// form={form}> reuses form.__rhf. Form.Item = RHF Controller cloning its child
// with value|checked + onChange bound to the field.

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyObj = Record<string, any>;

export interface AntdRule {
  required?: boolean;
  whitespace?: boolean;
  max?: number;
  min?: number;
  pattern?: RegExp;
  message?: string;
  // custom async validator: resolve = valid, reject(Error) = invalid.
  // Method-signature form so call sites typing `value` narrowly (e.g. string) are accepted.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validator?(rule: unknown, value: any): Promise<void>;
}

export interface FormInstance<T extends FieldValues = FieldValues> {
  submit: () => void;
  resetFields: () => void;
  getFieldValue: (name: string) => unknown;
  getFieldsValue: () => T;
  setFieldsValue: (values: Partial<T>) => void;
  setFieldValue: (name: string, value: unknown) => void;
  validateFields: () => Promise<T>;
  /** @internal */ __rhf: UseFormReturn<T>;
  /** @internal */ __onFinish: { current?: (values: T) => void };
}

export function useForm<T extends FieldValues = FieldValues>(): [FormInstance<T>] {
  // RHF's return object is stable across renders, so capturing it once is safe.
  const rhf = useRHF<T>({ mode: "onChange" });
  const onFinish = useRef<((values: T) => void) | undefined>(undefined);
  const ref = useRef<FormInstance<T> | undefined>(undefined);
  if (!ref.current) {
    ref.current = {
      __rhf: rhf,
      __onFinish: onFinish,
      submit: () => void rhf.handleSubmit(v => onFinish.current?.(v as T))(),
      resetFields: () => rhf.reset(),
      getFieldValue: name => rhf.getValues(name as never),
      getFieldsValue: () => rhf.getValues(),
      setFieldsValue: values =>
        Object.entries(values as AnyObj).forEach(([k, v]) => rhf.setValue(k as never, v as never)),
      setFieldValue: (name, value) => rhf.setValue(name as never, value as never),
      validateFields: async () => {
        const ok = await rhf.trigger();
        if (!ok) throw new Error("Validation failed");
        return rhf.getValues();
      },
    };
  }
  return [ref.current];
}

const FormCtx = createContext<UseFormReturn<AnyObj> | null>(null);

interface Col {
  span?: number;
}

export interface FormProps<T extends FieldValues = FieldValues> {
  form?: FormInstance<T>;
  layout?: "horizontal" | "vertical" | "inline";
  initialValues?: Partial<T>;
  onFinish?: (values: T) => void;
  size?: "small" | "middle" | "large";
  // Accepted for antd layout-only usage (label/field column split).
  labelCol?: Col;
  wrapperCol?: Col;
  children?: ReactNode;
  style?: CSSProperties;
  className?: string;
}

export function Form<T extends FieldValues = FieldValues>({
  form,
  initialValues,
  onFinish,
  layout = "horizontal",
  children,
  style,
  className,
}: FormProps<T>) {
  // Always call a local RHF (hooks can't be conditional) but prefer the passed form's.
  const local = useRHF<T>();
  const rhf = form?.__rhf ?? local;
  useEffect(() => {
    if (form) form.__onFinish.current = onFinish;
  }, [form, onFinish]);
  const inited = useRef(false);
  useEffect(() => {
    if (!inited.current && initialValues) {
      rhf.reset(initialValues as never);
      inited.current = true;
    }
  }, [initialValues, rhf]);
  return (
    <FormCtx.Provider value={rhf as UseFormReturn<AnyObj>}>
      <Box
        component="form"
        onSubmit={rhf.handleSubmit(v => onFinish?.(v as T))}
        sx={{
          display: "flex",
          flexDirection: layout === "inline" ? "row" : "column",
          gap: 1.5,
          flexWrap: layout === "inline" ? "wrap" : undefined,
        }}
        style={style}
        className={className}
        data-form-layout={layout}
      >
        {children}
      </Box>
    </FormCtx.Provider>
  );
}

function rulesToValidate(rules?: AntdRule[]): RegisterOptions | undefined {
  if (!rules?.length) return undefined;
  const opts: RegisterOptions = {};
  const validators: Record<string, (v: unknown) => boolean | string | Promise<boolean | string>> = {};
  rules.forEach((r, i) => {
    if (r.required) {
      opts.required = r.message ?? "This field is required";
    }
    if (r.whitespace) {
      validators[`whitespace_${i}`] = v =>
        typeof v !== "string" || v.trim().length > 0 || (r.message ?? "This field is required");
    }
    if (typeof r.max === "number") {
      opts.maxLength = { value: r.max, message: r.message ?? `Must be ${r.max} characters or fewer` };
    }
    if (r.pattern) {
      opts.pattern = { value: r.pattern, message: r.message ?? "Invalid format" };
    }
    if (r.validator) {
      validators[`validator_${i}`] = async v => {
        try {
          await r.validator!(r, v);
          return true;
        } catch (e) {
          return (e as Error)?.message || (r.message ?? "Invalid");
        }
      };
    }
  });
  if (Object.keys(validators).length) opts.validate = validators;
  return opts;
}

// antd/MUI child onChange handlers pass either a DOM event or a raw value; map
// both to the value RHF stores.
function extractValue(arg: unknown, valuePropName?: string): unknown {
  if (valuePropName === "checked") {
    if (typeof arg === "boolean") return arg;
    const t = (arg as AnyObj)?.target;
    return t && "checked" in t ? t.checked : arg;
  }
  const t = (arg as AnyObj)?.target;
  if (t && typeof t === "object" && "value" in t) return t.value;
  return arg;
}

export interface FormItemProps {
  name?: string;
  label?: ReactNode;
  rules?: AntdRule[];
  extra?: ReactNode;
  valuePropName?: string;
  required?: boolean;
  /** antd: render just the control (no label/margin wrapper) for inline/nested fields. */
  noStyle?: boolean;
  labelCol?: Col;
  wrapperCol?: Col;
  children?: ReactNode;
  style?: CSSProperties;
  className?: string;
}

const Item = ({ name, label, rules, extra, valuePropName, required, noStyle, labelCol, children, style, className }: FormItemProps) => {
  const ctx = useContext(FormCtx);
  const hasRequired = required || rules?.some(r => r.required);
  // htmlFor links the label to the control (which gets id={name} below), matching
  // antd Form.Item's label/control wiring so the field stays accessibly labelled
  // (getByLabelText, screen readers). Harmless when there's no name (layout-only).
  const labelNode = label != null && (
    <FormLabel htmlFor={name} required={hasRequired} sx={{ display: "block", mb: 0.5, fontSize: 13 }}>
      {label}
    </FormLabel>
  );

  // noStyle → render just the control, no label/margin wrapper (inline/nested fields).
  if (noStyle && (!name || !ctx)) {
    return <>{children}</>;
  }

  // No name or no form store → layout-only (label + child), matching antd's
  // label-only Form.Item usage. labelCol.span (of 24) sets a horizontal label width.
  if (!name || !ctx) {
    if (labelCol?.span) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }} style={style} className={className}>
          {label != null && (
            <FormLabel required={hasRequired} sx={{ minWidth: `${(labelCol.span / 24) * 100}%`, flexShrink: 0 }}>
              {label}
            </FormLabel>
          )}
          <Box sx={{ flex: 1 }}>{children}</Box>
        </Box>
      );
    }
    return (
      <Box sx={{ mb: 1 }} style={style} className={className}>
        {labelNode}
        {children}
        {extra != null && <FormHelperText>{extra}</FormHelperText>}
      </Box>
    );
  }

  return (
    <Controller
      name={name}
      control={ctx.control}
      rules={rulesToValidate(rules)}
      render={({ field, fieldState }) => {
        const injected =
          valuePropName === "checked"
            ? { checked: !!field.value }
            : { value: field.value ?? "" };
        const child = isValidElement(children)
          ? cloneElement(children as ReactElement<AnyObj>, {
              ...injected,
              name: field.name,
              // antd Form.Item wired id={name} onto the control; preserve that so
              // `#<fieldName>` selectors (e.g. #documentType, #date) resolve.
              id: (children.props as AnyObj).id ?? field.name,
              onChange: (arg: unknown) => {
                (children.props as AnyObj).onChange?.(arg);
                field.onChange(extractValue(arg, valuePropName));
              },
              onBlur: field.onBlur,
            })
          : children;
        // noStyle → bare control (plus inline validation), no label/margin wrapper.
        if (noStyle) {
          return (
            <>
              {child}
              {fieldState.error && (
                <FormHelperText error>{fieldState.error.message}</FormHelperText>
              )}
            </>
          );
        }
        return (
          <Box sx={{ mb: 1 }} style={style} className={className}>
            {labelNode}
            {child}
            {fieldState.error ? (
              <FormHelperText error>{fieldState.error.message}</FormHelperText>
            ) : (
              extra != null && <FormHelperText>{extra}</FormHelperText>
            )}
          </Box>
        );
      }}
    />
  );
};

// antd Form.useWatch(name, form) -> RHF useWatch({ name, control }).
export function useFormWatch<T extends FieldValues = FieldValues>(
  name: string,
  form: FormInstance<T>
): unknown {
  return useWatch({ name: name as never, control: form.__rhf.control });
}

Form.Item = Item;
Form.useForm = useForm;
Form.useWatch = useFormWatch;

export default Form;
